import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { supabase } from '../lib/supabase';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: 20px;
`;

const Card = styled.div`
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  padding: 30px;
  width: 100%;
  max-width: 400px;
`;

const Title = styled.h1`
  font-size: 24px;
  margin-bottom: 20px;
  text-align: center;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const Input = styled.input`
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
`;

const Button = styled.button`
  background-color: #4a90e2;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 10px;
  font-size: 16px;
  cursor: pointer;
  transition: background-color 0.3s;

  &:hover {
    background-color: #357ae8;
  }

  &:disabled {
    background-color: #cccccc;
    cursor: not-allowed;
  }
`;

const Message = styled.div<{ error?: boolean }>`
  margin-top: 15px;
  padding: 10px;
  border-radius: 4px;
  background-color: ${props => props.error ? '#ffebee' : '#e8f5e9'};
  color: ${props => props.error ? '#c62828' : '#2e7d32'};
  text-align: center;
`;

const ResetPassword: React.FC = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [message, setMessage] = useState<{ text: string; error: boolean } | null>(null);
  const [loading, setLoading] = useState(false);
  const [hash, setHash] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    // 从URL中获取重置密码的hash参数
    const hashFromUrl = window.location.hash.substring(1);
    if (hashFromUrl) {
      const params = new URLSearchParams(hashFromUrl);
      const accessToken = params.get('access_token');
      const type = params.get('type');
      
      if (accessToken && type === 'recovery') {
        setHash(accessToken);
      } else {
        setMessage({
          text: '无效的密码重置链接',
          error: true
        });
      }
    } else {
      setMessage({
        text: '无效的密码重置链接',
        error: true
      });
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      setMessage({
        text: '两次输入的密码不一致',
        error: true
      });
      return;
    }

    if (password.length < 6) {
      setMessage({
        text: '密码长度至少为6个字符',
        error: true
      });
      return;
    }

    setLoading(true);
    setMessage(null);

    try {
      const { error } = await supabase.auth.updateUser({
        password: password
      });

      if (error) {
        throw error;
      }

      setMessage({
        text: '密码已成功重置，请使用新密码登录',
        error: false
      });
      
      // 3秒后重定向到首页
      setTimeout(() => {
        navigate('/');
      }, 3000);
    } catch (error: any) {
      setMessage({
        text: error.message || '密码重置失败，请重试',
        error: true
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      <Card>
        <Title>重置密码</Title>
        
        {message && (
          <Message error={message.error}>
            {message.text}
          </Message>
        )}
        
        {!message?.error && (
          <Form onSubmit={handleSubmit}>
            <Input
              type="password"
              placeholder="新密码"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={6}
            />
            <Input
              type="password"
              placeholder="确认新密码"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              minLength={6}
            />
            <Button type="submit" disabled={loading}>
              {loading ? '处理中...' : '重置密码'}
            </Button>
          </Form>
        )}
      </Card>
    </Container>
  );
};

export default ResetPassword; 